<template>
  <div class="verification-detail">
    <div class="box summary-box">
      <div class="summary-header">
        <h1 class="title is-5 mb-0">Verification results</h1>
        <div class="summary-badges">
          <b-tag :type="statusType">{{ statusLabel }}</b-tag>
          <b-tag
            v-if="['complete', 'no_match'].includes(verification.status)"
            type="is-primary is-light"
          >
            {{ patients.length }} {{ patients.length === 1 ? 'match' : 'matches' }}
          </b-tag>
        </div>
      </div>
      <div class="summary-details is-size-7 has-text-grey">
        <span>
          Submitted:
          {{ verification.created_at | formatDate }}
        </span>
        <span>
          Submitted by:
          {{ verification.user || '—' }}
        </span>
        <template v-if="adminMode">
          <span>
            <strong>Source:</strong>
            {{ verification.result_source === 'saved' ? 'Saved response' : 'Provider' }}
          </span>
          <span v-if="verification.provider_responded_at">
            <strong>Original provider response:</strong>
            {{ verification.provider_responded_at | formatDateTime }}
          </span>
        </template>
      </div>
      <b-collapse
        v-if="Object.keys(submissionFields).length"
        class="submitted-information"
        :open="false"
      >
        <template #trigger="props">
          <button type="button" class="section-toggle" :aria-expanded="props.open">
            <span>Search criteria</span>
            <b-icon :icon="props.open ? 'chevron-up' : 'chevron-down'" size="is-small" />
          </button>
        </template>
        <dl class="submitted-fields mt-2">
          <div v-for="(value, key) in submissionFields" :key="key" class="submitted-field">
            <dt>{{ key }}</dt>
            <dd>{{ value }}</dd>
          </div>
        </dl>
      </b-collapse>
    </div>

    <b-message
      v-if="adminMode && verification.result_source === 'saved'"
      type="is-info"
      :closable="false"
    >
      This search reused a saved response for the same criteria. Its original response date is shown
      above.
    </b-message>
    <b-message v-if="verification.status === 'pending'" type="is-info" :closable="false">
      {{
        refreshing
          ? 'Checking this pending verification…'
          : adminMode
            ? 'This verification is pending.'
            : 'This verification is still pending. Reopen this result later to check again.'
      }}
      Checking an existing result does not use another verification.
    </b-message>
    <b-message v-if="error" type="is-danger" :closable="false">{{ error }}</b-message>
    <b-message v-if="verification.status === 'error'" type="is-danger" :closable="false">
      <strong>Verification could not be completed.</strong>
      {{ responseMessage }}
    </b-message>
    <b-message v-if="verification.status === 'no_match'" type="is-info" :closable="false">
      <strong>No matching patients found.</strong>
      Review the submitted information and correct any details before starting a new search.
    </b-message>

    <template v-if="patients.length">
      <b-collapse
        v-for="(patient, index) in patients"
        :key="`${verification.id}-${index}`"
        class="patient-card"
      >
        <template #trigger="props">
          <button
            type="button"
            class="patient-header has-background-primary-light"
            :aria-expanded="props.open"
          >
            <span class="patient-heading">
              <span class="patient-name">
                <b-tooltip
                  class="match-score"
                  label="Match score"
                  type="is-dark"
                  position="is-top"
                  :triggers="['hover', 'focus']"
                  append-to-body
                  role="img"
                  :aria-label="
                    patient.matchScore == null
                      ? 'Match score unavailable'
                      : `Match score: ${patient.matchScore} out of 99`
                  "
                >
                  <span
                    class="score-ring"
                    :style="{
                      '--score': patient.matchScore == null ? 0 : (patient.matchScore / 99) * 100,
                    }"
                    aria-hidden="true"
                  >
                    <span>{{ patient.matchScore == null ? '—' : patient.matchScore }}</span>
                  </span>
                </b-tooltip>
                <span class="has-text-weight-semibold has-text-primary-dark">
                  {{ patient.FirstName }} {{ patient.MiddleName }} {{ patient.LastName }}
                </span>
              </span>
              <span v-if="patient.DOB" class="is-size-7 has-text-grey">DOB: {{ patient.DOB }}</span>
            </span>
            <b-icon :icon="props.open ? 'chevron-up' : 'chevron-down'" size="is-small" />
          </button>
        </template>
        <div class="patient-content columns">
          <div class="column is-half">
            <dl>
              <div
                v-for="(value, label) in {
                  'First name': patient.FirstName,
                  'Middle name': patient.MiddleName,
                  'Last name': patient.LastName,
                  'Date of birth': patient.DOB,
                  SSN: patient.SSN,
                }"
                :key="label"
                class="info-row"
              >
                <dt class="info-label">{{ label }}</dt>
                <dd class="info-value">{{ value == null || value === '' ? '—' : value }}</dd>
              </div>
            </dl>
          </div>
          <div class="column is-half">
            <dl>
              <div
                v-for="(value, label) in {
                  'Street address': (patient.Address || {}).AddressLine1,
                  'Address line 2': (patient.Address || {}).AddressLine2,
                  City: (patient.Address || {}).City,
                  State: (patient.Address || {}).State,
                  'ZIP code': (patient.Address || {}).ZipCode,
                }"
                :key="label"
                class="info-row"
              >
                <dt class="info-label">{{ label }}</dt>
                <dd class="info-value">{{ value == null || value === '' ? '—' : value }}</dd>
              </div>
              <div v-if="patient.Address && patient.Address.ReportedDate" class="info-row">
                <dt class="info-label">Address reported</dt>
                <dd class="info-value">{{ patient.Address.ReportedDate }}</dd>
              </div>
            </dl>
          </div>
        </div>
        <div v-if="!adminMode" class="patient-actions">
          <span class="is-size-7 has-text-grey">
            Use this patient's details to start a Blanket VOB search.
          </span>
          <b-button
            tag="router-link"
            type="is-primary"
            size="is-small"
            icon-left="card-account-details"
            :to="{
              name: 'BlanketVOBCreate',
              query: { verification_id: verification.id, patient_index: String(index) },
            }"
          >
            Rerun as Blanket VOB
          </b-button>
        </div>
      </b-collapse>
    </template>

    <div v-if="adminMode" class="box mt-5">
      <b-collapse :open="false">
        <template #trigger="props">
          <button type="button" class="section-toggle" :aria-expanded="props.open">
            <span>Full response</span>
            <b-icon :icon="props.open ? 'chevron-up' : 'chevron-down'" size="is-small" />
          </button>
        </template>
        <pre class="response-json mt-3">{{ verification.response | pretty }}</pre>
        <template
          v-if="verification.remote_errors && Object.keys(verification.remote_errors).length"
        >
          <h2 class="title is-6 mt-4">Response messages</h2>
          <pre class="response-json">{{ verification.remote_errors | pretty }}</pre>
        </template>
      </b-collapse>
    </div>
  </div>
</template>
<script>
export default {
  name: 'PatientVerificationDetail',
  props: {
    verification: { type: Object, required: true },
    adminMode: { type: Boolean, default: false },
    refreshing: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },
  computed: {
    statusLabel() {
      return (
        {
          complete: 'Complete',
          no_match: 'No match',
          pending: 'Pending',
          error: 'Unable to verify',
        }[this.verification.status] || this.verification.status
      );
    },
    statusType() {
      return (
        {
          complete: 'is-success is-light',
          no_match: 'is-info is-light',
          pending: 'is-warning is-light',
          error: 'is-danger is-light',
        }[this.verification.status] || 'is-light'
      );
    },
    patients() {
      if (this.verification.status !== 'complete') return [];
      const patients = this.verification.response?.Patients || this.verification.response?.patients;
      return Array.isArray(patients)
        ? patients
            .filter((patient) => patient && typeof patient === 'object' && !Array.isArray(patient))
            .map((patient) => ({
              ...patient,
              matchScore:
                patient.Score != null &&
                String(patient.Score).trim() !== '' &&
                Number.isFinite(Number(patient.Score))
                  ? Math.min(99, Math.max(0, Number(patient.Score)))
                  : null,
            }))
        : [];
    },
    responseMessage() {
      const response = this.verification.response || {};
      const message = response.Error || response.error;
      return typeof message === 'string' && message.trim()
        ? message
        : 'Please review the submitted information or contact support for help.';
    },
    submissionFields() {
      const s = this.verification.submission || {};
      const a = s.Address || {};
      const fields = {
        'First name': s.FirstName,
        'Middle name': s.MiddleName,
        'Last name': s.LastName,
        'Date of birth': s.DOB,
        SSN: s.SSN,
        'Street address': a.AddressLine1,
        'Address line 2': a.AddressLine2,
        City: a.City,
        State: a.State,
        ZIP: a.ZipCode,
      };
      return Object.fromEntries(Object.entries(fields).filter(([, value]) => value));
    },
  },
};
</script>
<style scoped>
.summary-box {
  padding: 1.5rem;
}
.summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.summary-badges {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.summary-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
}
.section-toggle,
.patient-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.section-toggle,
.patient-header {
  width: 100%;
  border: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.section-toggle {
  padding: 0.25rem 0;
  background: transparent;
  color: #495057;
  font-size: 0.875rem;
  font-weight: 600;
}
.section-toggle:focus-visible,
.patient-header:focus-visible {
  outline: 2px solid #7957d5;
  outline-offset: -2px;
}
.submitted-information {
  border-top: 1px solid #e9ecef;
  margin-top: 1rem;
  padding-top: 0.75rem;
}
.submitted-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.submitted-field {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.25rem 0.5rem;
  max-width: 100%;
  padding: 0.35rem 0.65rem;
  border: 1px solid #edeaf4;
  border-radius: 4px;
  background: #faf9fc;
  font-size: 0.75rem;
}
.submitted-field dt {
  color: #6b7280;
}
.submitted-field dd {
  margin: 0;
  color: #2c3e50;
  font-weight: 600;
  overflow-wrap: anywhere;
}
.patient-card {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 1rem;
}
.patient-header {
  padding: 0.75rem 1rem;
  border-left: 3px solid #7957d5;
}
.patient-heading {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  min-width: 0;
  overflow-wrap: anywhere;
}
.patient-name {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
}
.match-score {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.score-ring {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: conic-gradient(#7957d5 calc(var(--score) * 1%), #e4e0ed 0);
  color: #503396;
  font-size: 0.75rem;
  font-weight: 700;
}
.score-ring::before {
  content: '';
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: #fff;
}
.score-ring span {
  position: relative;
}
.patient-content.columns {
  margin: 0;
  padding: 0.25rem;
}
.patient-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid #edeaf4;
}
.patient-actions .button {
  flex-shrink: 0;
}
.info-row {
  display: grid;
  grid-template-columns: 8rem minmax(0, 1fr);
  gap: 0.75rem;
  padding: 0.45rem 0;
  border-bottom: 1px solid #eef0f3;
  font-size: 0.875rem;
}
.info-row:last-child {
  border-bottom: 0;
}
.info-label {
  color: #6b7280;
}
.info-value {
  margin: 0;
  color: #2c3e50;
  font-weight: 500;
  overflow-wrap: anywhere;
}
.response-json {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 40rem;
  overflow-y: auto;
}
@media (max-width: 768px) {
  .summary-box {
    padding: 1rem;
  }
  .patient-heading {
    flex-direction: column;
  }
  .info-row {
    grid-template-columns: 7rem minmax(0, 1fr);
  }
}
@media (max-width: 480px) {
  .patient-actions {
    flex-direction: column;
    align-items: stretch;
  }
  .patient-actions .button {
    width: 100%;
  }
}
</style>
